import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SECTORS } from "@/data/company";
import BankingPartners from "@/components/BankingPartners";

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* HEADER */}
      <section className="pt-28 lg:pt-36 pb-20 lg:pb-28 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Services & Sectors</div>
            <h1
              data-testid="services-title"
              className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]"
            >
              Where we invest and how we partner.
            </h1>
          </div>
          <div className="lg:col-span-7">
            <p className="text-slate-700 text-lg leading-relaxed">
              We focus on five enduring sectors where structural demand,
              regulation, and operator quality create the conditions for
              long-duration compounding. In each, we partner with management
              teams who bring sector expertise and operational depth.
            </p>
            <p className="mt-6 text-slate-500 leading-relaxed">
              Our capital is patient, our structures bespoke, and our
              involvement actively governance-led — not transactional.
            </p>
          </div>
        </div>
      </section>

      {/* SECTOR DEEP DIVES */}
      <section className="bg-white border-y border-slate-200">
        {SECTORS.map((s, idx) => {
          const reversed = idx % 2 === 1;
          return (
            <article
              key={s.key}
              data-testid={`service-detail-${s.key}`}
              className={`border-b border-slate-200 last:border-b-0`}
            >
              <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28">
                <div
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                    reversed ? "lg:flex-row-reverse" : ""
                  }`}
                >
                  <div
                    className={`lg:col-span-5 ${
                      reversed ? "lg:order-2" : ""
                    }`}
                  >
                    <div className="aspect-[4/5] w-full overflow-hidden border border-slate-200 relative bg-slate-950">
                      {s.image ? (
                        <img
                          src={s.image}
                          alt={s.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <s.Icon
                            size={120}
                            strokeWidth={0.8}
                            className="text-emerald-400/70"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className={`lg:col-span-7 ${
                      reversed ? "lg:order-1" : ""
                    }`}
                  >
                    <div className="overline">
                      Sector 0{idx + 1}
                    </div>
                    <div className="mt-4 flex items-center gap-4">
                      <s.Icon
                        size={28}
                        strokeWidth={1.4}
                        className="text-emerald-800"
                      />
                      <h2 className="font-serif text-3xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05]">
                        {s.title}
                      </h2>
                    </div>
                    <p className="mt-6 text-slate-700 text-lg leading-relaxed">
                      {s.summary}
                    </p>
                    <p className="mt-4 text-slate-500 leading-relaxed">
                      {s.detail}
                    </p>
                    <ul className="mt-8 space-y-3">
                      {s.theses.map((t) => (
                        <li
                          key={t}
                          className="flex items-start gap-3 text-slate-800"
                        >
                          <Check
                            size={18}
                            className="mt-0.5 text-emerald-800 flex-none"
                          />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <BankingPartners />

      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center">
          <div className="overline">Engagement</div>
          <h2 className="mt-6 font-serif text-3xl lg:text-4xl tracking-tight text-slate-900">
            Explore a partnership with Finovest.
          </h2>
          <Link
            to="/contact"
            data-testid="services-cta-contact"
            className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase font-semibold bg-slate-900 text-white px-7 py-4 hover:bg-slate-800 transition-colors"
          >
            Speak with us <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
